import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

function ProfileInfoCard({ title, description, info, action, shadow, onSave, isAdmin, date }) {
  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState(info);

  useEffect(() => {
    setFormValues(info);
  }, [info]);

  const labels = [];
  const { socialMediaColors } = colors;
  const { size } = typography;

  Object.keys(info).forEach((el) => {
    if (el.match(/[A-Z\s]+/)) {
      const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
      const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);
      labels.push(newElement);
    } else {
      labels.push(el);
    }
  });

  const handleChange = (key, value) => {
    setFormValues({ ...formValues, [key]: value });
  };

  const handleSave = () => {
    onSave(formValues);
    setEditMode(false);
  };

  const renderItems = labels.map((label, key) => (
    <MDBox key={label} display="flex" py={1} pr={2}>
      <MDTypography variant="button" fontWeight="bold" textTransform="capitalize">
        {label}: &nbsp;
      </MDTypography>
      {editMode ? (
        <TextField
          variant="standard"
          type={!date ? "text" : "date"}
          value={formValues[Object.keys(info)[key]] || ""}
          onChange={(e) => handleChange(Object.keys(info)[key], e.target.value)}
          disabled={!isAdmin && label === "Document Expire Date"}
        />
      ) : (
        <MDTypography variant="button" fontWeight="regular" color="text">
          &nbsp;{formValues[Object.keys(info)[key]]}
        </MDTypography>
      )}
    </MDBox>
  ));

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      {title ? (
        <MDBox display="flex" alignItems="center" pt={2} px={2}>
          <MDTypography pr={1} variant="h6" fontWeight="medium" textTransform="capitalize">
            {title}
          </MDTypography>
          <Tooltip title={action.tooltip} placement="top">
            <Icon
              onClick={() => {
                setEditMode(!editMode);
              }}
              style={{ cursor: "pointer" }}
            >
              edit
            </Icon>
          </Tooltip>
        </MDBox>
      ) : (
        <MDBox />
      )}
      <MDBox p={2}>
        {title ? (
          <MDBox mb={2} lineHeight={1}>
            <MDTypography variant="button" color="text" fontWeight="light">
              {description}
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox />
        )}
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>
        {title ? (
          <MDBox>{renderItems}</MDBox>
        ) : (
          <MDBox display="flex">
            {renderItems}
            {isAdmin ? (
              <Tooltip title={action.tooltip} position="top">
                <Icon
                  onClick={() => {
                    setEditMode(!editMode);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  edit
                </Icon>
              </Tooltip>
            ) : (
              ""
            )}
          </MDBox>
        )}
        {editMode && (
          <MDBox mt={2}>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              <MDTypography
                variant="h6"
                color="white"
                fontWeight="medium"
                textTransform="capitalize"
              >
                Save
              </MDTypography>
            </Button>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

ProfileInfoCard.defaultProps = {
  shadow: true,
  isAdmin: false,
};

ProfileInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  action: PropTypes.shape({
    route: PropTypes.string,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  shadow: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  date: PropTypes.bool,
};

export default ProfileInfoCard;
